<script>
    import {
        Button,
        Loader,
        NotificationModal,
        PageHead,
    } from "../../Components/Common/index.svelte";

    import CreateAccountUser from "./components/CreateAccountUser.svelte";
    import CreateAccountDetails from "./components/CreateAccountDetails.svelte";
    import CreateAccountProvider from "./components/CreateAccountProvider.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        goToURL,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import {
        defaultAccountUnderpaymentThreshold,
        defaultCalendarEventColor,
    } from "../../Utils/utilConstants.svelte";

    import { cacheAccountPrePopulateSearchOptions } from "../../Utils/utilCache.svelte";

    let userSectionRef, accountSectionRef, providerSectionRef;

    let userDetails = {},
        accountDetails = {
            claimDelayHours: Number(24),
            calendarColour: defaultCalendarEventColor,
            status: Number(0),
            title: 5, // Dr
            underPayThreshold: defaultAccountUnderpaymentThreshold,
        },
        providerDetails = {
            isFullyCoveredHBFWA: 0
        };

    let isCreatingNewAccountModalOpen = false,
        isCreating = false,
        isCreatingUser = false,
        isCreatingAccount = false;

    const checkDetailsValidation = async () => {
        const userDetailsCheck = await userSectionRef.checkUserValidation();

        if (!userDetailsCheck) return;

        const accountDetailsCheck =
            await accountSectionRef.checkAccountValidation();

        if (!accountDetailsCheck) return;

        const providerDetailsCheck =
            await providerSectionRef.checkProviderValidation();

        if (!providerDetailsCheck) return;

        await createProcess();
    };

    const createProcess = async () => {
        isCreating = true;

        _openCreateNewAccountModal();

        await createUser();

        if (userDetails.id) await createAccount();

        isCreating = false;
    };

    const createUser = async () => {
        isCreatingUser = true;

        await apiMethods.postMethod
            .signUp(userDetails)
            .then((res) => {
                if (!res || !res.id || res.error) {
                    showError(res.error);
                    isCreatingNewAccountModalOpen = false;
                } else userDetails = { ...res };
            })
            .catch((err) => {
                showException(err);
                isCreatingNewAccountModalOpen = false;
            });

        isCreatingUser = false;
    };

    const createAccount = async () => {
        isCreatingAccount = true;

        const accountObj = {
            ...accountDetails,
            createdUserId: userDetails.id,
            users: [{ id: userDetails.id }],
            providers: [{ ...providerDetails, doctorId: userDetails.id }],
            profile: {
                ahpra: "MED" + accountDetails.ahpra.replaceAll("MED", ""),
                abn: accountDetails.abn,
                bankAccountName: accountDetails.bankAccountName,
                bankAccountBSB: accountDetails.bankAccountBSB,
                bankAccountNumber: accountDetails.bankAccountNumber,
                qualifications: accountDetails.qualifications,
            },
        };

        await apiMethods.postMethod
            .createAccount(accountObj)
            .then(async (res) => {
                if (!res || !res.id || res.error)
                    showError(
                        res.error ??
                            "Failed to create account, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    accountDetails = { ...res };

                    // update pre-populate doctor search options
                    await cacheAccountPrePopulateSearchOptions(true);
                }
            })
            .catch((err) => showException(err));

        isCreatingAccount = false;
    };

    const goToViewPage = () => {
        goToURL(`/Account/view/${accountDetails.id}`);
    };

    // HELPER FUNCTIONS
    const _openCreateNewAccountModal = () =>
        (isCreatingNewAccountModalOpen = true);

    const _closeCreateNewAccountModal = () => {
        isCreatingNewAccountModalOpen = false;
        isCreating = false;
    };
</script>

<PageHead title="Create New Doctor" />

<div class="flex flex-1 relative">
    <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 m-4">
        <CreateAccountUser bind:this={userSectionRef} bind:userDetails />
        <CreateAccountDetails
            bind:this={accountSectionRef}
            bind:accountDetails
        />
        <CreateAccountProvider
            bind:this={providerSectionRef}
            bind:providerDetails
        />
    </div>
</div>

<div class="flex items-center justify-end">
    <Button clickFunc={checkDetailsValidation}>Create</Button>
</div>

<NotificationModal
    title={`${isCreating ? "Processing..." : "Done"}`}
    isModalOpen={isCreatingNewAccountModalOpen}
    showCancelBtn={false}
    hideCrossButton
    preventESCClose
    showConfirmBtn={!isCreating}
    confirmAction={goToViewPage}
    submitText="Done"
>
    <div class="flex flex-col flex-1 gap-y-4">
        <div class="flex items-center gap-x-4">
            <div class="w-8 h-8">
                {#if isCreatingUser}
                    <Loader />
                {:else if userDetails.id}
                    <div class="flex flex-1 items-center">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
                            fill="none"
                            viewBox="0 0 24 24"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            /></svg
                        >
                    </div>
                {/if}
            </div>
            <p class="leading-6 text-sm font-bold">
                {isCreatingUser ? "Creating user details" : "User created"}
            </p>
        </div>

        <div class="flex items-center gap-x-4">
            <div class="w-8 h-8">
                {#if isCreatingAccount}
                    <Loader />
                {:else if accountDetails.id}
                    <div class="flex flex-1 items-center">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
                            fill="none"
                            viewBox="0 0 24 24"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            /></svg
                        >
                    </div>
                {/if}
            </div>
            <p class="leading-6 text-sm font-bold">
                {isCreatingAccount
                    ? "Await"
                    : isCreatingAccount
                      ? "Creating account details"
                      : "Account created"}
            </p>
        </div>
    </div>
</NotificationModal>
